/** @jsx jsx */
import { jsx } from '@emotion/core';
import { FC, ReactNode, useState, useEffect, Fragment, useMemo } from 'react';
import orderBy from 'lodash/orderBy';
import { Box, Divider, Grid, Paper, Typography } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import useContentMgmt from '../../../../hooks/useContentMgmt';
import microcopyGroupIds from '../../../pge-plus-common/microcopyGroupIds';
import {
  EvConsultDiscussionTopic,
  EvConsultProvidedDetails,
  HvConsultationService,
  HvConsultDiscussionTopic,
  HvConsultProvidedDetails,
  PreferredDay,
  PreferredTime,
  RequestCategory,
  Request,
  YesNoNaResponse,
  ContactMethod,
  CustomerPgePlusUseIntentTimeframe,
  RequestStatus,
  EnergyTrustRebates,
} from '../../../../__generated__/pgeplus-types';
import { useLocation } from '@reach/router';
import useGetConsultationRequests from '../../../../hooks/pge-plus/useGetConsultationRequests';
import mainColors from '../../../../themes/main-colors';
import useUtil from '../../../../hooks/pge-plus/useUtil';
import useWrapWithLoader from '../../../../hooks/useWrapWithLoading';
import PGEButton from '../../../buttons/PGE-Button';
import { navigate } from 'gatsby';
import theme from '../../../../themes/theme';
import routes from '../../../../routes';
import { getFormattedDateFromUTCSting } from '../../../../util/date-utils';

interface SummaryProps {
  children?: ReactNode;
  path: string;
}
interface LocationState {
  requestId: string;
  showContactSummary: boolean;
}

interface SummaryItem {
  label: string | undefined;
  values: (string | undefined | null)[];
}

const useStyles = makeStyles(() =>
  createStyles({
    submitBack: {
      borderRadius: '30px',
      minWidth: '130px',
      [theme.breakpoints.down('md')]: {
        width: '100%',
      },
    },
    container: {
      marginBottom: 80,
      justifyContent: 'center',
      [theme.breakpoints.down('md')]: {
        flexDirection: 'column-reverse',
      },
    },
    gridItem: {
      display: 'flex',
      justifyContent: 'center',
      flexDirection: 'row',
      alignItems: 'center',
      gap: 32,
    },
    label: {
      wordWrap: 'break-word',
      width: '85%',
      fontSize: 20,
      fontWeight: 'bold',
      color: mainColors.grey4,
      marginTop: 20,
      marginBottom: 1,
      [`@media (max-width: ${theme.breakpoints.values.sm}px)`]: {
        fontSize: 16,
      },
    },
    value: {
      padding: '30px 25px',
      border: `1px solid ${mainColors.noirBlur}`,
    },
    item: {
      wordWrap: 'break-word',
      fontSize: 20,
      color: mainColors.noirBlur,
      marginTop: 1,
      '&:first-child': {
        marginTop: 1,
      },
      [`@media (max-width: ${theme.breakpoints.values.sm}px)`]: {
        fontSize: 16,
      },
    },
  }),
);

const preferredDayOrder: Record<PreferredDay, number> = {
  [PreferredDay.Monday]: 1,
  [PreferredDay.Tuesday]: 2,
  [PreferredDay.Wednesday]: 3,
  [PreferredDay.Thursday]: 4,
  [PreferredDay.Friday]: 5,
  [PreferredDay.NoPreference]: 6,
};

const preferredTimeOrder: Record<PreferredTime, number> = {
  [PreferredTime.Morning]: 1,
  [PreferredTime.Afternoon]: 2,
  [PreferredTime.Evening]: 3,
  [PreferredTime.NoPreference]: 4,
};

export const ViewConsultationApplicationSummary: FC<SummaryProps> = props => {
  const location = useLocation();
  const { setErrorNotification } = useUtil();
  const { wrapWithLoader } = useWrapWithLoader();
  const [request, setRequest] = useState<Request | null>(null);

  const locationState = location?.state as LocationState;
  const requestId = locationState.requestId || '';
  const { content } = useContentMgmt(microcopyGroupIds.PGE_PLUS_CONSULTATION);
  const classes = useStyles();

  const { getConsultationRequest } = useGetConsultationRequests();
  useEffect(() => {
    if (requestId) {
      void getRequestDetails();
    } else {
      void navigate(routes.PGE_PLUS_EV_CHARGERS_WELCOME_BACK);
    }
  }, [locationState]);

  const getRequestDetails = wrapWithLoader(async () => {
    try {
      const results = await getConsultationRequest({
        requestId: requestId,
      });
      setRequest(results?.data?.getRequest?.request);
    } catch (e) {
      setErrorNotification(true);
    }
  });

  const DayOfTheWeek = {
    [PreferredDay.Monday]: content.get('REUSABLE_DAY_MONDAY') || '',
    [PreferredDay.Tuesday]: content.get('REUSABLE_DAY_TUESDAY') || '',
    [PreferredDay.Wednesday]: content.get('REUSABLE_DAY_WEDNESDAY') || '',
    [PreferredDay.Thursday]: content.get('REUSABLE_DAY_THURSDAY') || '',
    [PreferredDay.Friday]: content.get('REUSABLE_DAY_FRIDAY') || '',
    [PreferredDay.NoPreference]: content.get('NO_PREFERENCE') || '',
  };
  const TimeOfTheDay = {
    [PreferredTime.Morning]: content.get('TIME_SLOT_1') || '',
    [PreferredTime.Afternoon]: content.get('TIME_SLOT_2') || '',
    [PreferredTime.Evening]: content.get('TIME_SLOT_3') || '',
    [PreferredTime.NoPreference]: content.get('NO_PREFERENCE') || '',
  };

  const HvServicesOptions = {
    [HvConsultationService.Heating]: content.get('HEATING') || '',
    [HvConsultationService.Cooling]: content.get('COOLING') || '',
    [HvConsultationService.Both]: content.get('HEATING_AND_COOLING') || '',
  };
  const HvDiscussionTopics = {
    [HvConsultDiscussionTopic.PgePlus]:
      content.get('HOW_DOES_PGE_PLUS_WORK') || '',
    [HvConsultDiscussionTopic.SmartThermostatProgram]:
      content.get('SMART_THERMOSTAT_PROGRAM') || '',
    [HvConsultDiscussionTopic.PeakTimeRebates]:
      content.get('PEAK_TIME_REBATES') || '',
    [HvConsultDiscussionTopic.EnergySavings]:
      content.get('ENERGY_SAVINGS') || '',
    [HvConsultDiscussionTopic.SoundLevel]: content.get('SOUND_LEVEL') || '',
    [HvConsultDiscussionTopic.AllergenAndVirusFiltration]:
      content.get('ALLERGEN_AND_VIRUS_FILTRATION') || '',
    [HvConsultDiscussionTopic.HumidityControl]:
      content.get('HUMIDITY_CONTROL') || '',
    [HvConsultDiscussionTopic.BasicOperation]:
      content.get('BASIC_OPERATION') || '',
    [HvConsultDiscussionTopic.Other]: content.get('OTHER') || '',
  };

  const EvDiscussionTopics = {
    [EvConsultDiscussionTopic.PgePlus]:
      content.get('HOW_DOES_PGE_PLUS_WORK') || '',
    [EvConsultDiscussionTopic.SmartChargingProgram]:
      content.get('SMART_CHARGING_PROGRAM') || '',
    [EvConsultDiscussionTopic.TimeOfDay]: content.get('TIME_OF_DAY') || '',
    [EvConsultDiscussionTopic.LevelOneVsLevelTwo]:
      content.get('LVL_1_LVL_2') || '',
    [EvConsultDiscussionTopic.ProductSelection]:
      content.get('PRODUCT_SELECTION') || '',
    [EvConsultDiscussionTopic.Installation]: content.get('INSTALLATION') || '',
    [EvConsultDiscussionTopic.PanelUpgrade]: content.get('PANEL_UPGRADE') || '',
    [EvConsultDiscussionTopic.Other]: content.get('OTHER') || '',
  };

  const YesNoOption = {
    [YesNoNaResponse.Yes]: content.get('REUSABLE_YES') || '',
    [YesNoNaResponse.No]: content.get('REUSABLE_NO') || '',
    [YesNoNaResponse.NotApplicable]:
      content.get('REUSABLE_NOT_APPLICABLE') || '',
  };

  const ContactMethodOption = {
    [ContactMethod.Phone]: content.get('REUSABLE_PHONE') || '',
    [ContactMethod.Email]: content.get('REUSABLE_EMAIL') || '',
  };

  const CustomerIntentToUsePGEPlusOptions = {
    [CustomerPgePlusUseIntentTimeframe.NextSixMonths]: content.get(
      'OPTION_NEXT_SIX_MONTHS',
    ),
    [CustomerPgePlusUseIntentTimeframe.SixMonthsToAYear]: content.get(
      'OPTION_SIX_MONTHS_TO_A_YEAR',
    ),
    [CustomerPgePlusUseIntentTimeframe.OneYearAndMore]: content.get(
      'OPTION_ONE_YEAR_AND_MORE',
    ),
    [CustomerPgePlusUseIntentTimeframe.NeverCustomerAlreadyHadInstaller]: content.get(
      'OPTION_NEVER_CUSTOMER_ALREADY_HAD_OWN_INSTALLER',
    ),
    [CustomerPgePlusUseIntentTimeframe.NeverServiceNotValuable]: content.get(
      'OPTION_NEVER_SERVICE_NOT_VALUABLE',
    ),
  };

  const Relationship = {
    parent: content.get('RELATIONSHIP_PARENT') || '',
    sibling: content.get('RELATIONSHIP_SIBLING') || '',
    spouse: content.get('RELATIONSHIP_SPOUSE') || '',
    partner: content.get('RELATIONSHIP_PARTNER') || '',
    friend: content.get('RELATIONSHIP_FRIEND') || '',
    other: content.get('RELATIONSHIP_OTHER') || '',
    self: content.get('RELATIONSHIP_SELF') || '',
  };

  const TrustRebates = {
    [EnergyTrustRebates.CentralAirConditioner]:
      content.get('CENTRAL_AIR_CONDITIONER') || '',
    [EnergyTrustRebates.DuctedHeatPump]: content.get('DUCTED_HEAT_PUMP') || '',
    [EnergyTrustRebates.DuctlessHeatPump]:
      content.get('DUCTLESS_HEAT_PUMP') || '',
    [EnergyTrustRebates.NotApplicable]: content.get('REUSABLE_NOT_APPLICABLE'),
  };

  const providedEvDetails = request?.providedDetails as EvConsultProvidedDetails & {
    evDiscussionTopics: EvConsultDiscussionTopic[];
  };
  const providedHvDetails = request?.providedDetails as HvConsultProvidedDetails & {
    hvDiscussionTopics: HvConsultDiscussionTopic[];
  };
  const evTopcicsInterestedIn = providedEvDetails?.evDiscussionTopics
    ? providedEvDetails?.evDiscussionTopics?.map(
        topic => EvDiscussionTopics[topic as keyof typeof EvDiscussionTopics],
      )
    : [];
  const hvTopcicsInterestedIn = providedHvDetails?.hvDiscussionTopics
    ? providedHvDetails?.hvDiscussionTopics?.map(
        topic => HvDiscussionTopics[topic as keyof typeof HvDiscussionTopics],
      )
    : [];

  const summaryItems: SummaryItem[] = [
    {
      label: content.get('CATEGORY'),
      values: [content.get('REQUEST_CATEGORY_' + request?.category)],
    },
    ...(request?.accountContact
      ? [
          {
            label: content.get('CONTACT_INFORMATION'),
            values: [
              `${request?.accountContact?.firstName} ${request?.accountContact?.lastName}`,
              `${request?.accountContact?.address?.addressLine1}, ${request?.accountContact?.address?.city}, ${request?.accountContact?.address?.state}, ${request?.accountContact?.address?.postal}`,
              `${request?.accountContact?.emailAddress}`,
              `${request?.accountContact?.phoneNumber}`,
            ],
          },
        ]
      : []),
    {
      label: content.get('HVAC_MAIN_POINT_OF_CONTACT'),
      values: [
        `${request?.userContactDetails?.firstName} ${request?.userContactDetails?.lastName}`,
        `${request?.userContactDetails?.email}`,
        `${request?.userContactDetails?.phone}`,
        Relationship[
          request?.userContactDetails?.relationship as keyof typeof Relationship
        ] || '',
      ],
    },
    {
      label: content.get('LANGUAGE_PREFERENCE_FOR_CONSULTATION'),
      values: [`${content.get(request?.language || '')}`],
    },
    {
      label: content.get('BEST_TIME_TO_CONTACT'),
      values: [
        [...(request?.providedDetails?.preferredDay || [])]
          //Sort by weekday (Mon, Tue, Wed etc)
          ?.sort(
            (a, b) =>
              (preferredDayOrder[a!] ?? 0) - (preferredDayOrder[b!] ?? 0),
          )
          ?.map(day => DayOfTheWeek[day as keyof typeof DayOfTheWeek])
          .join(', '),
        [...(request?.providedDetails?.preferredTime || [])]
          ?.sort(
            (a, b) =>
              (preferredTimeOrder[a!] ?? 0) - (preferredTimeOrder[b!] ?? 0),
          )
          ?.map(timeSlot => TimeOfTheDay[timeSlot as keyof typeof TimeOfTheDay])
          .join(', '),
      ],
    },
    ...(request?.category === RequestCategory.HvacConsultation
      ? [
          {
            label: content.get('CONSULTATION_WHAT_ARE_YOU_LOOKING_FOR'),
            values: [
              HvServicesOptions[
                providedHvDetails?.consultationService as keyof typeof HvServicesOptions
              ] || '',
            ],
          },
        ]
      : []),
    {
      label: content.get('INTERESTED_IN_DISCUSSING'),
      values: [
        ...evTopcicsInterestedIn,
        ...hvTopcicsInterestedIn,
        request?.providedDetails?.otherDiscussion
          ? `${content.get('ADDITIONAL_COMMENTS')}: ${
              request?.providedDetails?.otherDiscussion
            }`
          : '',
      ],
    },
  ];

  const mostRecentContactAttempt = useMemo(
    () =>
      request?.contactAttempts?.length
        ? orderBy(request?.contactAttempts, ['updatedAt'], ['desc'])[0]
        : null,
    [request?.contactAttempts],
  );

  const contactSummary: SummaryItem[] = [
    ...(request?.assignedUserName
      ? [
          {
            label: content.get('PGE_PLUS_OPERATIONS_USER_NAME'),
            values: [request.assignedUserName],
          },
        ]
      : []),
    ...(request?.contactAttempts?.length
      ? [
          {
            label: content.get('CONTACT_METHOD'),
            values: [
              mostRecentContactAttempt?.contactMethod
                .map(i => ContactMethodOption[i])
                .join(', '),
            ],
          },
          {
            label: content.get('CONTACT_DATE'),
            values: [
              getFormattedDateFromUTCSting(
                mostRecentContactAttempt?.contactDate,
                'MM/dd/yyyy',
              ),
            ],
          },
        ]
      : []),
    ...(request?.operatorProvidedDetails.hasMainElectricalPanelConcerns !== null
      ? [
          {
            label: content.get('CUSTOMER_HAS_MAIN_PANEL_CONCERNS'),
            values: [
              request?.operatorProvidedDetails.hasMainElectricalPanelConcerns
                ? content.get('REUSABLE_YES')
                : content.get('REUSABLE_NO'),
            ],
          },
        ]
      : []),
    ...(request?.operatorProvidedDetails
      ?.hasRecommendedMainElectricalPanelUpgrade
      ? [
          {
            label: content.get('RECOMMENDED_ELECTRIC_PANEL_UPGRADE'),
            values: [
              YesNoOption[
                request?.operatorProvidedDetails
                  ?.hasRecommendedMainElectricalPanelUpgrade
              ],
            ],
          },
        ]
      : []),
    ...(request?.operatorProvidedDetails?.recommendedEnergyTrustRebates !== null
      ? [
          {
            label: content.get('RECOMMENDED_ENERGY_TRUST_REBATES'),
            values: [
              TrustRebates[
                request?.operatorProvidedDetails
                  ?.recommendedEnergyTrustRebates as keyof typeof TrustRebates
              ],
            ],
          },
        ]
      : []),
    ...(request?.visibleComments?.length
      ? [
          {
            label: content.get('CUSTOMER_NOTES_COLON'),
            values: [request?.visibleComments?.join('\n')!],
          },
        ]
      : []),
    ...(request?.operatorProvidedDetails?.hasRecommendedInstallReferral !== null
      ? [
          {
            label: content.get('RECOMMENDED_INSTALLER_REFERRAL'),
            values: [
              YesNoOption[
                request?.operatorProvidedDetails
                  ?.hasRecommendedInstallReferral as keyof typeof YesNoOption
              ],
            ],
          },
        ]
      : []),
    ...(request?.operatorProvidedDetails?.hasRecommendedInstallReferral ===
    YesNoNaResponse.Yes
      ? [
          {
            label: content.get('RECOMMENDED_INSTALLER_INFORMATION'),
            values: [
              request?.operatorProvidedDetails.recommendedInstallerInformation
                ?.name,
              request?.operatorProvidedDetails.recommendedInstallerInformation
                ?.phone,
              request?.operatorProvidedDetails.recommendedInstallerInformation
                ?.email,
            ],
          },
        ]
      : []),
    ...(request?.operatorProvidedDetails.customerIntentToUsePGEPlus
      ? [
          {
            label: content.get('HOW_SERIOUS_IS_CUSTOMER_TO_USE_PGEPLUS'),
            values: [
              CustomerIntentToUsePGEPlusOptions[
                request?.operatorProvidedDetails.customerIntentToUsePGEPlus
              ],
            ],
          },
        ]
      : []),
  ];

  function renderSummaryItems(items: SummaryItem[]) {
    return items.map(item => (
      <Box key={item.label} css={{ padding: '0px 18px 0 36px' }}>
        <Typography variant={'h4'} className={classes.label}>
          {item.label}
        </Typography>
        {item.values.map(val => (
          <Typography variant="body1" className={classes.item}>
            {val}
          </Typography>
        ))}
      </Box>
    ));
  }

  return (
    <Fragment>
      <Grid
        xs={12}
        md={9}
        css={{ maxWidth: 790, margin: '35px auto 0px auto' }}
      >
        <Box my={4}>
          <Box my={5}>
            <Typography variant={'h1'} css={{ textAlign: 'center' }}>
              {content.get('HVAC_YOUR_SUBMITTED_APPLICATION')}
            </Typography>
          </Box>
          <Box
            mt={2.5}
            css={{
              marginBottom: 20,
            }}
          >
            <Paper className={classes.value}>
              <Box sx={{ padding: '0px 16px' }}>
                <Typography
                  variant={'h4'}
                  className={classes.label}
                  css={{ color: mainColors.midGrey }}
                >
                  {content.get('CONSULTATION_FORM_RESPONSES')}
                </Typography>
                {renderSummaryItems(summaryItems)}
                {request?.status === RequestStatus.Completed &&
                  locationState.showContactSummary &&
                  contactSummary.length > 0 && (
                    <Fragment>
                      <Divider css={{ marginTop: '20px' }} />
                      <Typography
                        variant={'h4'}
                        className={classes.label}
                        css={{ color: mainColors.midGrey }}
                      >
                        {content.get('CONTACT_SUMMARY')}
                      </Typography>
                      {renderSummaryItems(contactSummary)}
                    </Fragment>
                  )}
              </Box>
            </Paper>
          </Box>
          <Box display={'flex'} justifyContent={'center'}></Box>
        </Box>
      </Grid>
      <Grid container className={classes.container}>
        <Grid
          item
          className={classes.gridItem}
          css={{
            marginRight: 16,
          }}
        >
          <PGEButton
            fullWidth
            type={'button'}
            variant="outlined"
            color="secondary"
            className={classes.submitBack}
            onClick={() =>
              void navigate(routes.PGE_PLUS_EV_CHARGERS_WELCOME_BACK)
            }
          >
            {content.get('BACK')}
          </PGEButton>
        </Grid>
      </Grid>
    </Fragment>
  );
};
